import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { getConsentAuditReport } from "@/lib/consent/audit.functions";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/admin_/privacy")({
  component: PrivacyAuditPage,
  head: () => ({
    meta: [
      { title: "Permission review · TruWe Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function csvEscape(value: unknown): string {
  const s = value == null ? "" : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function PrivacyAuditPage() {
  const fetchReport = useServerFn(getConsentAuditReport);
  const { data, isLoading, error } = useQuery({
    queryKey: ["consent-audit"],
    queryFn: () => fetchReport({}),
  });

  const download = () => {
    if (!data) return;
    const header = ["contact_id", "source", "phone", "email", "first_seen", "last_seen", "whatsapp_flag"];
    const lines = [
      header.join(","),
      ...data.unclearSample.map((r) =>
        [r.id, r.source, r.phone, r.email, r.firstSeen, r.lastSeen, r.whatsappFlag]
          .map(csvEscape)
          .join(","),
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `truwe-unclear-permissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="mx-auto w-full max-w-4xl px-4 py-10">
      <h1 className="font-display text-2xl font-semibold text-foreground">Permission review</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Where each contact came from, and whether we hold an explicit, recorded permission to
        send them offers. Contacts marked unclear must be reviewed by a person before any
        promotional send — an old list is never treated as consented.
      </p>

      {isLoading ? <p className="mt-8 text-sm text-muted-foreground">Loading…</p> : null}
      {error ? (
        <p className="mt-8 text-sm text-destructive">
          Could not load the report. You need admin access to view it.
        </p>
      ) : null}

      {data ? (
        <>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ["Contacts", data.totals.contacts],
              ["Explicit permission", data.totals.explicit],
              ["Declined", data.totals.declined],
              ["Unclear", data.totals.unclear],
            ].map(([label, value]) => (
              <div key={String(label)} className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
                <p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 overflow-hidden rounded-xl border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Contacts</th>
                  <th className="px-4 py-3">Explicit</th>
                  <th className="px-4 py-3">Declined</th>
                  <th className="px-4 py-3">Unclear</th>
                </tr>
              </thead>
              <tbody>
                {data.bySource.map((g) => (
                  <tr key={g.source} className="border-t border-border">
                    <td className="px-4 py-3 font-medium text-foreground">{g.source}</td>
                    <td className="px-4 py-3">{g.total}</td>
                    <td className="px-4 py-3">{g.explicit}</td>
                    <td className="px-4 py-3">{g.declined}</td>
                    <td className="px-4 py-3">{g.unclear}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Button className="mt-6" onClick={download} disabled={data.unclearSample.length === 0}>
            Export unclear permissions (CSV)
          </Button>
        </>
      ) : null}
    </main>
  );
}
